<template>
  <q-toolbar class="auto-height" :class="!isAdmin ? 'func-btn' : ''">
    <TemplateButtons
      v-model="value"
      :isAdmin="isAdmin"
      :isSuper="isSuper"
      :isChanged="isChanged"
      :imgFile="imgFile"
      :previewImageUrl="previewImageUrl"
      :altText="altText"
      @initMessage="initMessage"
      @onAddTemplate="captureMessage"
      @update:altText="(val) => $emit('update:altText', val)"
    />
    <HelperButtons
      v-model="value"
      :selectedNode="selectedNode"
      :selectedPath="selectedPath"
      :nodePath="nodePath"
      :listNodePath="listNodePath"
      :isAdmin="isAdmin"
      :errorPath="errorPath"
      @onSelectNode="onSelectNode"
      @onFlipNode="onFlipNode"
      @onResetNode="onResetNode"
      ref="helperButtons"
      v-if="!isAdmin"
    />
  </q-toolbar>
  <div class="col flex-message-content" v-if="value">
    <FlexMessage
      ref="flexMessage"
      :type="'bubble'"
      :bubble-message="value"
      :hoverPath="currentHovePath"
      :editPath="selectedPath"
      :isAdmin="isAdmin"
      :isChanged="isChanged"
      @onSelectNode="onSelectNode"
      @initListNode="initListNode"
      @onCaptureMessage="getMessageImage"
      @update:hoverPath="(args) => (currentHovePath = args)"
      :key="componentKeyBubble"
      v-if="value.type === 'bubble'"
    />
    <FlexMessage
      ref="flexMessage"
      :type="'carousel'"
      :carousel-message="value"
      :hoverPath="currentHovePath"
      :isChanged="isChanged"
      :editPath="selectedPath"
      :isAdmin="isAdmin"
      @onCaptureMessage="getMessageImage"
      @onSelectNode="onSelectNode"
      @initListNode="initListNode"
      @update:hoverPath="(args) => (currentHovePath = args)"
      :key="componentKeyCarousel"
      v-else
    />
  </div>
  <div class="flex-message-content" v-else></div>
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component'
import { maska } from 'maska'
import { Prop } from 'vue-property-decorator'
import FlexMessage from '@/components/common/flex-message/FlexMessage.vue'
import {
  IFlexMessageBubble,
  IFlexMessageButton,
  IFlexMessageCarousel,
  IFlexMessageIcon,
  IFlexMessageImage,
  IFlexMessageSpan,
  IFlexMessageText,
} from '@/utils/types'
import cloneDeep from 'lodash/cloneDeep'
import TemplateButtons from '@/components/common/flex-message/functions/TemplateButtons.vue'
import HelperButtons from '@/components/common/flex-message/functions/HelperButtons.vue'

@Options({
  components: {
    HelperButtons,
    TemplateButtons,
    FlexMessage,
  },
  directives: { maska },
  emits: ['update:modelValue', 'initMessage', 'update:hoverPath', 'onSelectNode', 'update:altText', 'captureMessage'],
})
export default class PreviewPanel extends Vue {
  @Prop()
  modelValue!: IFlexMessageBubble | IFlexMessageCarousel

  @Prop()
  hoverPath!: string

  @Prop()
  isChanged!: boolean

  @Prop()
  isAdmin!: boolean

  @Prop()
  isSuper!: boolean

  @Prop()
  errorPath!: string[]

  @Prop()
  altText!: string

  selectedNode: IFlexMessageButton | IFlexMessageIcon | IFlexMessageImage | IFlexMessageSpan | IFlexMessageText = {}
  selectedPath = ''
  listNodePath: string[] = []
  componentKeyBubble = 0
  componentKeyCarousel = 0
  imgFile: File | null = null
  previewImageUrl = ''
  action = 'new'

  get value() {
    return this.modelValue
  }

  set value(value: IFlexMessageBubble | IFlexMessageCarousel) {
    this.$emit('update:modelValue', value)
  }

  get currentHovePath(): string {
    return this.hoverPath
  }

  set currentHovePath(value: string) {
    this.$emit('update:hoverPath', value)
  }

  get nodePath() {
    if (this.selectedPath === '') return []
    const keys = this.selectedPath.replace(/(box|text)-(\d+)/g, 'contents.$2').split('.')

    if (this.modelValue) {
      if (this.modelValue.type === 'bubble') {
        keys.shift()
      } else {
        const firstKey = keys.shift()
        if (firstKey) {
          keys.unshift('contents', firstKey)
        } else {
          keys.unshift('contents')
        }
      }
    }
    return keys
  }

  initMessage(message) {
    this.value = cloneDeep(message)
    this.listNodePath = []
    this.componentKeyBubble++
    this.componentKeyCarousel++
    this.$emit('initMessage', message)
    if (!this.isAdmin) {
      this.$nextTick(() => {
        this.$refs.helperButtons.initMessage(message)
      })
    }
  }

  onSelectNode(node, path) {
    if (this.errorPath.length > 0) return
    this.selectedPath = path
    if (path === '') {
      this.listNodePath = []
      this.componentKeyBubble++
      this.componentKeyCarousel++
    }
    if (node) {
      this.selectedNode = node
    } else {
      this.selectedNode = this.getNodeValue()
    }
    this.$emit('onSelectNode', this.selectedPath, this.selectedNode, this.nodePath)
  }

  onFlipNode(newIndex) {
    this.selectedPath = this.selectedPath.replace(/(\D)(\d+)(?=\.[a-zA-Z]+$)/, `$1${newIndex}`)
    this.onResetNode()
  }

  onResetNode() {
    this.listNodePath = []
    this.componentKeyBubble++
    this.componentKeyCarousel++
    this.$emit('onSelectNode', this.selectedPath, this.selectedNode, this.nodePath)
  }

  initListNode(path) {
    this.listNodePath = path
  }

  getNodeValue() {
    const keys = cloneDeep(this.nodePath)
    const lastKey = keys[keys.length - 1]
    if (this.value.type === 'bubble') {
      if (lastKey === undefined) {
        return this.value
      } else if (!['header', 'hero', 'body', 'footer'].includes(lastKey)) {
        keys.pop()
      }
    } else {
      if (lastKey === 'carousel') {
        return this.value
      } else if (['box', 'image', 'text', 'button', 'filler', 'separator', 'video', 'span', 'icon'].includes(lastKey)) {
        keys.pop()
      }
    }
    if (['header', 'hero', 'body', 'footer'].includes(lastKey)) {
      if (this.value.type !== 'bubble') {
        keys.pop()
      }
    }
    return keys.reduce((acc, key) => acc && acc[key], this.value)
  }

  captureMessage() {
    this.$refs.flexMessage.captureMessage()
  }

  getMessageImage(file, url) {
    this.imgFile = file
    this.previewImageUrl = url
    this.$emit('captureMessage', this.imgFile, this.previewImageUrl)
  }

  mounted() {
    if (this.value && !this.isAdmin) {
      this.$nextTick(() => {
        this.$refs.helperButtons.initMessage(this.value)
      })
    }
  }
}
</script>

<style scoped lang="scss">
.flex-message-content {
  background-color: #7491bd;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  height: calc(100vh - 48px);
  overflow-y: auto;
}
.title-plus .flex-message-content {
  height: calc(100vh - 97px);
}
.func-btn {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.func-btn > :first-child {
  border-bottom: 1px solid #d0d2e0;
}
@media screen and (min-width: 1024px) {
  .flex-message-content {
    height: calc(100vh - 120px);
  }
  .title-plus .flex-message-content {
    height: calc(100vh - 170px);
  }
}
</style>
